import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "#/hooks/ToastContext";
import { qk } from "#/lib/queryKeys";
import useAdminTemplatesApi, {
  AdminTemplateCreateRequest,
  AdminTemplateUpdateRequest,
} from "#/admin/templates/hooks/useAdminTemplatesApi";

type UpdateTemplatePayload = {
  id: string;
  payload: AdminTemplateUpdateRequest;
};

export function useAdminTemplateMutations() {
  const api = useAdminTemplatesApi();
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const createTemplate = useMutation({
    mutationFn: (payload: AdminTemplateCreateRequest) =>
      api.createTemplate(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["message-templates"] });
      showToast("Draft template created.", "success");
    },
    onError: (error) => {
      const message =
        error instanceof Error
          ? error.message
          : "Unable to create the template.";
      showToast(message, "error");
    },
  });

  const updateTemplate = useMutation({
    mutationFn: ({ id, payload }: UpdateTemplatePayload) =>
      api.updateTemplate(id, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["message-templates"] });
      queryClient.invalidateQueries({
        queryKey: qk.messageTemplateDetail(data.id),
      });
      showToast("Draft template updated.", "success");
    },
    onError: (error) => {
      const message =
        error instanceof Error
          ? error.message
          : "Unable to update the template.";
      showToast(message, "error");
    },
  });

  return { createTemplate, updateTemplate };
}

export default useAdminTemplateMutations;
